import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { toast } from "react-toastify";
import moment from "moment";
import { useSelector } from "react-redux";
import TextField from "../common/TextField";
import Button from "../common/Button";
import CustomDatePicker from "../common/CustomDatePicker";
import AsyncSelect from "../common/asyncSelect";
import SingleSelect from "../common/SingleSelect";
import {
  QUOTE_STATUS,
  applyAccountToForm,
  buildQuoteRecord,
  calcLine,
  calcTotals,
  clearAccountFromForm,
  createCancelToken,
  defaultTerms,
  emptyLineItem,
  getInitialForm,
  getQuoteStatusActor,
  isQuoteManualInvoiceCreated,
  isRequestCanceled,
  quoteToForm,
  quoteToManualInvoiceForm,
  resolveQuoteStatus,
  resolveVatPercentage,
  resolveVatRate,
  storePreview,
} from "./quoteUtils";
import { checkPrivileges } from "../../Helpers";
import {
  getExpenseSubTypes,
  getExpenseTypesByContract,
  loadAccountForQuote,
  saveQuote,
  searchAccounts,
  searchSalesPeople,
} from "./quotesApi";
import VehicleDetailsSection from "./VehicleDetailsSection";
import CurrencySymbol from "../common/CurrencySymbol";

const withSelectError = (hasError, styles = {}) => ({
  ...styles,
  control: (base, state) => {
    const extra =
      typeof styles.control === "function"
        ? styles.control(base, state)
        : { ...base, ...(styles.control || {}) };
    return {
      ...extra,
      borderColor: hasError ? "#ff424c" : extra.borderColor,
      "&:hover": {
        ...(extra["&:hover"] || {}),
        borderColor: hasError ? "#ff424c" : extra.borderColor,
      },
    };
  },
});

const Q = {
  canvas: "#f4f7f8",
  line: "#e2e9ef",
  navy: "#1a1d4f",
  muted: "#6b7280",
};

const cardSx = {
  bgcolor: "#fff",
  border: "1px solid",
  borderColor: Q.line,
  borderRadius: "12px",
};

const SectionCard = ({ title, children }) => (
  <Paper
    elevation={0}
    sx={{ ...cardSx, p: "16px 18px 8px", mb: 1, overflow: "visible" }}
  >
    {title ? (
      <Typography sx={{ mb: 1.5, fontSize: 14, fontWeight: 700, color: Q.navy }}>
        {title}
      </Typography>
    ) : null}
    {children}
  </Paper>
);

const CreateUpdateForm = ({
  translate,
  mode,
  selectedQuote,
  onSaved,
  onConvertToManualInvoice,
}) => {
  const logInUser = useSelector((state) => state.logInUsers);
  const account = useSelector((state) => state.ServerSetting);
  const currencyLabel = logInUser?.attributes?.currencyLabel || "AED";
  const isCreate = mode === "create";
  const [form, setForm] = useState(() =>
    selectedQuote
      ? quoteToForm(selectedQuote, logInUser)
      : getInitialForm(logInUser),
  );
  const [readOnly, setReadOnly] = useState(mode === "view");
  const [accounts, setAccounts] = useState([]);
  const [salesPeople, setSalesPeople] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [lineOptions, setLineOptions] = useState({});
  const [subTypeLoading, setSubTypeLoading] = useState({});
  const [allExpenseTypes, setAllExpenseTypes] = useState([]);
  const [resultMessage, setResultMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [savedQuote, setSavedQuote] = useState(selectedQuote || null);
  const accountLoadId = useRef(0);

  const quoteLockStatus = resolveQuoteStatus(selectedQuote || savedQuote);
  const isStatusLocked =
    quoteLockStatus === QUOTE_STATUS.APPROVED ||
    quoteLockStatus === QUOTE_STATUS.REJECTED;

  useEffect(() => {
    // Approved / rejected quotes stay permanently read-only
    setReadOnly(mode === "view" || isStatusLocked);
  }, [mode, isStatusLocked]);

  useEffect(() => {
    if (!selectedQuote) return;
    const mapped = quoteToForm(selectedQuote, logInUser);
    setForm(mapped);
    setSavedQuote(selectedQuote);

    let cancelled = false;
    (async () => {
      let contractNumber =
        mapped.contractNumber || mapped.account?.contractNumber || "";
      let account = mapped.account;

      if (
        selectedQuote.accountName &&
        (!account?.id || !contractNumber)
      ) {
        try {
          const rows = await searchAccounts(
            selectedQuote.accountName,
            logInUser.id,
          );
          if (cancelled) return;
          const match =
            (rows || []).find(
              (row) =>
                String(row.name || row.label || "")
                  .toLowerCase()
                  .trim() ===
                String(selectedQuote.accountName).toLowerCase().trim(),
            ) || rows?.[0];
          if (match?.id) {
            const { details, terms } = await loadAccountForQuote(match);
            if (cancelled) return;
            contractNumber =
              details?.contractNumber ||
              match.contractNumber ||
              contractNumber;
            account = {
              ...(details || match),
              id: details?.id || match.id,
              label: details?.name || match.label || selectedQuote.accountName,
              name: details?.name || match.name || selectedQuote.accountName,
              contractNumber,
              vatPercentage: resolveVatPercentage(details || match),
            };
            const lineVatRate = resolveVatRate(account);
            setForm((prev) => ({
              ...prev,
              account,
              contractNumber,
              vatPercentage: account.vatPercentage,
              contactPerson:
                prev.contactPerson ||
                details?.contact ||
                details?.accountContact ||
                "",
              mobile: prev.mobile || details?.phone || details?.mobile || "",
              email: prev.email || details?.email || "",
              address:
                prev.address ||
                details?.attributes?.c_address ||
                details?.address ||
                "",
              vatNumber:
                prev.vatNumber ||
                details?.attributes?.c_VATNumber ||
                details?.vatNumber ||
                "",
              items: (prev.items || []).map((row) =>
                calcLine(row, lineVatRate),
              ),
              ...terms,
            }));
          }
        } catch (error) {
          // keep mapped account name only
        }
      }

      // if (contractNumber) {
        await loadExpenseTypes();
      // }
      if (cancelled) return;
      const typeIds = [
        ...new Set(
          (mapped.items || [])
            .map((row) => row.expenseTypeId)
            .filter(Boolean)
            .map(String),
        ),
      ];
      await Promise.all(
        typeIds.map((id) => fetchSubExpenseTypes(id)),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedQuote?.id]);

  const clearFieldError = (...keys) => {
    setFieldErrors((prev) => {
      if (!prev || !keys.some((key) => prev[key])) return prev;
      const next = { ...prev };
      keys.forEach((key) => {
        delete next[key];
      });
      return next;
    });
  };

  const clearItemError = (index, field) => {
    setFieldErrors((prev) => {
      const items = prev.items;
      if (!items?.[index]?.[field]) return prev;
      const nextItems = items.map((row, i) => {
        if (i !== index) return row;
        const copy = { ...row };
        delete copy[field];
        return copy;
      });
      return { ...prev, items: nextItems };
    });
  };
  const totals = useMemo(() => calcTotals(form.items), [form.items]);
  const vatPercentage = resolveVatPercentage(
    form.vatPercentage ?? form.account,
  );
  const quoteMeta = savedQuote || selectedQuote;
  const expenseTypeSource = useRef(null);
  const subTypeFetchCache = useRef({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await fetchAccounts("");
      if (cancelled) return;
      await fetchSalesPeople("", form.salesPerson);
      if (cancelled) return;
      // Edit load is handled by selectedQuote effect — avoid duplicate expense calls
      if (selectedQuote) return;
      // const contractNumber =
        // form.contractNumber || form.account?.contractNumber;
      // if (contractNumber)
         await loadExpenseTypes();
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchAccounts = async (search) => {
    const rows = await searchAccounts(search, logInUser.id);
    setAccounts(rows);
    return rows;
  };

  const fetchSalesPeople = async (search, selectedPerson) => {
    const rows = await searchSalesPeople(search, logInUser);
    const withSelected =
      selectedPerson?.id &&
      !rows.some((row) => String(row.id) === String(selectedPerson.id))
        ? [
            {
              id: selectedPerson.id,
              value: selectedPerson.id,
              name: selectedPerson.name || selectedPerson.label || "",
              label: selectedPerson.label || selectedPerson.name || "",
              email: selectedPerson.email || form.salesPersonEmail || "",
              phone:
                selectedPerson.phone ||
                selectedPerson.mobile ||
                form.salesPersonMobile ||
                "",
            },
            ...rows,
          ]
        : rows;
    setSalesPeople(withSelected);
    return withSelected;
  };

  const fillAccountDetails = async (account) => {
    const loadId = ++accountLoadId.current;
    if (!account) {
      loadExpenseTypes(null);
      setForm(clearAccountFromForm);
      setAccountLoading(false);
      return;
    }
    setAccountLoading(true);
    try {
      const { details, terms } = await loadAccountForQuote(account);
      if (loadId !== accountLoadId.current) return;
      // const contractNumber = details.contractNumber || account.contractNumber;
      await loadExpenseTypes();
      if (loadId !== accountLoadId.current) return;
      setForm((prev) => applyAccountToForm(prev, details, account, terms));
    } catch (error) {
      if (loadId !== accountLoadId.current) return;
      setForm((prev) => applyAccountToForm(prev, account, account, defaultTerms()));
    } finally {
      if (loadId === accountLoadId.current) setAccountLoading(false);
    }
  };

  const loadExpenseTypes = async (cn="bfmc000") => {
    if (expenseTypeSource.current) {
      expenseTypeSource.current.cancel();
    }
    if (!cn) {
      setAllExpenseTypes([]);
      setLineOptions({});
      return;
    }
    expenseTypeSource.current = createCancelToken();
    try {
      const rows = await getExpenseTypesByContract(
        cn,
        expenseTypeSource.current.token,
      );
      setAllExpenseTypes(rows);
    } catch (error) {
      if (!isRequestCanceled(error)) setAllExpenseTypes([]);
    }
  };

  const fetchSubExpenseTypes = async (expenseTypeId, search = "") => {
    if (!expenseTypeId) return [];
    const cacheKey = `${expenseTypeId}::${String(search || "").trim()}`;
    const cached = subTypeFetchCache.current[cacheKey];
    if (cached) return cached;

    const request = (async () => {
      setSubTypeLoading((prev) => ({ ...prev, [expenseTypeId]: true }));
      try {
        const rows = await getExpenseSubTypes(expenseTypeId, search);
        setLineOptions((prev) => ({ ...prev, [expenseTypeId]: rows }));
        return rows;
      } catch (error) {
        delete subTypeFetchCache.current[cacheKey];
        return [];
      } finally {
        setSubTypeLoading((prev) => ({ ...prev, [expenseTypeId]: false }));
      }
    })();

    subTypeFetchCache.current[cacheKey] = request;
    return request;
  };

  const searchExpenseSubTypes = async (inputValue, expenseType) => {
    const expenseTypeId = expenseType?.id || expenseType;
    if (!expenseTypeId) return [];
    const search = inputValue || "";
    // Prefer already-loaded options for empty search (edit open / menu open)
    if (
      !search &&
      Array.isArray(lineOptions[expenseTypeId]) &&
      lineOptions[expenseTypeId].length
    ) {
      return lineOptions[expenseTypeId];
    }
    const rows = await fetchSubExpenseTypes(expenseTypeId, search);
    setResultMessage(rows.length === 0 ? "No option found" : "");
    return rows;
  };

  const handleChange = (name, value) => {
    if (name === "account") {
      if (value && typeof value === "object") {
        fillAccountDetails(value);
        clearFieldError("account");
      } else if (!value) {
        fillAccountDetails(null);
      }
      return;
    }
    if (name === "salesPerson") {
      clearFieldError("salesPerson");
      const selected = value && typeof value === "object" ? value : null;
      setForm((prev) => ({
        ...prev,
        salesPerson: selected,
        salesPersonEmail: selected?.email || "",
        salesPersonMobile: selected?.phone || selected?.mobile || "",
      }));
      if (selected?.id) {
        setSalesPeople((prev) => {
          if (prev.some((row) => String(row.id) === String(selected.id))) {
            return prev;
          }
          return [
            {
              id: selected.id,
              value: selected.id,
              name: selected.name || selected.label || "",
              label: selected.label || selected.name || "",
              email: selected.email || "",
              phone: selected.phone || selected.mobile || "",
            },
            ...prev,
          ];
        });
      }
      return;
    }
    if (value) clearFieldError(name);
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLineChange = (field, value, index) => {
    const errorField = field === "item" || field === "subTypes" ? "itemName" : field;
    if (value) clearItemError(index, errorField);
    setForm((prev) => {
      const items = [...prev.items];
      const lineVatRate = resolveVatRate(prev.vatPercentage ?? prev.account);
      if (field === "serviceType") {
        const selected =
          value && typeof value === "object"
            ? value
            : allExpenseTypes.find(
                (option) => String(option.id) === String(value),
              ) || null;
        items[index] = calcLine(
          {
            ...emptyLineItem(),
            serviceType: selected?.name || selected?.categoryName || "",
            expenseType: selected,
            expenseTypeId: selected?.id || null,
            typeLabel: selected?.name || selected?.label || "",
          },
          lineVatRate,
        );
        if (selected?.id) fetchSubExpenseTypes(selected.id);
      } else if (field === "item" || field === "subTypes") {
        items[index] = calcLine(
          {
            ...items[index],
            item: value,
            itemId: value?.id,
            expenseTypeId:
              items[index].expenseTypeId ||
              value?.parentId ||
              value?.expenseTypeId ||
              null,
            expenseSubTypeId: value?.id || null,
            itemName: value?.label || value?.name || "",
            itemCode: value?.subTypeNum || "",
            typeLabel:
              items[index].typeLabel ||
              value?.categoryName ||
              items[index].serviceType ||
              "",
            subTypeLabel: value?.label || value?.name || "",
            subTypeNum: value?.subTypeNum || "",
            unitPrice: parseFloat(value?.unitPrice) || 0,
            cost: parseFloat(value?.unitPrice) || parseFloat(value?.cost) || 0,
            description:
              value?.description ||
              value?.itemDescription ||
              value?.remarks ||
              value?.label ||
              value?.name ||
              items[index].description ||
              "",
            quantity: items[index].quantity || 1,
          },
          lineVatRate,
        );
      } else if (field === "withVat") {
        items[index] = calcLine(
          { ...items[index], withVat: value },
          lineVatRate,
        );
      } else {
        items[index] = calcLine(
          { ...items[index], [field]: value },
          lineVatRate,
        );
      }
      return { ...prev, items };
    });
  };

  const addLine = () =>
    setForm((prev) => ({ ...prev, items: [...prev.items, emptyLineItem()] }));

  const removeLine = (index) => {
    if (form.items.length === 1) {
      toast.error(translate("cantDeleteLastRow"));
      return;
    }
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const collectErrors = () => {
    const errors = {};
    const missing = [];
    const mark = (key, label) => {
      errors[key] = true;
      missing.push(label);
    };
    if (!form.account) mark("account", translate("accountName"));
    if (!form.quoteRefNo) {
      mark("quoteRefNo", translate("quoteRefNo") || translate("quoteNumber"));
    }
    if (!form.contactPerson) mark("contactPerson", translate("contactPerson"));
    if (!form.mobile) mark("mobile", translate("mobileNumber"));
    if (!form.expiryDate) mark("expiryDate", translate("quotationExpiry"));
    // Vehicle is optional — only validate fields the user actually filled in
    const vin = String(form.vin || "").trim();
    if (vin && (vin.length !== 17 || !/^[A-Z0-9]{17}$/.test(vin))) {
      mark("vin", translate("vin"));
    }
    if (form.year) {
      const yearNum = parseInt(form.year, 10);
      const currentYear = new Date().getFullYear();
      if (
        Number.isNaN(yearNum) ||
        yearNum < 1950 ||
        yearNum > currentYear + 1
      ) {
        mark("year", translate("year"));
      }
    }
    if (!form.salesPerson) mark("salesPerson", translate("salesManager"));
    const itemErrors = form.items.map((row) => {
      const rowErr = {};
      if (!row.serviceType && !row.expenseTypeId) rowErr.serviceType = true;
      if (!row.itemName) rowErr.itemName = true;
      if (!row.quantity) rowErr.quantity = true;
      if (row.unitPrice === "" || row.unitPrice === null || row.unitPrice === undefined) {
        rowErr.unitPrice = true;
      }
      return rowErr;
    });
    const hasItemErrors = itemErrors.some((row) => Object.keys(row).length);
    if (hasItemErrors) {
      errors.items = itemErrors;
      missing.push(translate("lineItems"));
    }
    return { errors, missing, ok: missing.length === 0 };
  };

  const persist = async () => {
    if (isStatusLocked) {
      toast.error(translate("approvedOrRejectedQuoteCannotEdit"));
      return;
    }
    const { errors, missing, ok } = collectErrors();
    if (!ok) {
      setFieldErrors(errors);
      toast.error(
        `${translate("pleaseFillRequiredFields")}: ${missing.join(", ")}`,
      );
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      const saved = await saveQuote(form, {
        id: quoteMeta?.id,
        quoteNumber: form.quoteRefNo || quoteMeta?.quoteNumber || quoteMeta?.quoteRefNo,
        quoteRefNo: form.quoteRefNo || quoteMeta?.quoteRefNo || quoteMeta?.quoteNumber,
        quoteDate: quoteMeta?.quoteDate,
        createdAt: quoteMeta?.createdAt || quoteMeta?.created,
        createdById: logInUser?.id,
        createdByName: logInUser?.name || logInUser?.email,
        companyId: account?.companyId,
        vehicleId: form.vehicleId || quoteMeta?.vehicleId || null,
        status: quoteMeta?.status || QUOTE_STATUS.DRAFT,
        attributes: quoteMeta?.attributes || {},
        approvedBy: quoteMeta?.approvedBy || quoteMeta?.attributes?.approvedBy,
        rejectedBy: quoteMeta?.rejectedBy || quoteMeta?.attributes?.rejectedBy,
        approvedAt: quoteMeta?.approvedAt || quoteMeta?.attributes?.approvedAt,
        rejectedAt: quoteMeta?.rejectedAt || quoteMeta?.attributes?.rejectedAt,
      });
      setSavedQuote(saved);
      setReadOnly(mode === "view");
      toast.success(
        isCreate && !quoteMeta?.quoteNumber
          ? translate("quotationCreated")
          : translate("quotationUpdated"),
      );
      if (onSaved) onSaved(saved);
    } catch (error) {
      toast.error(translate("somethingWentWrong"));
    } finally {
      setSaving(false);
    }
  };

  const currentStatus = resolveQuoteStatus(quoteMeta || {});
  const statusActor = getQuoteStatusActor(quoteMeta);

  const preview = () => {
    const quote = buildQuoteRecord(form, {
      id: quoteMeta?.id || "preview",
      quoteNumber: form.quoteRefNo || quoteMeta?.quoteNumber || quoteMeta?.quoteRefNo || "DRAFT",
      quoteRefNo: form.quoteRefNo || quoteMeta?.quoteRefNo || "DRAFT",
      quoteDate: quoteMeta?.quoteDate || moment().toISOString(),
      createdByName: logInUser?.name || logInUser?.email,
      vehicleId: form.vehicleId || quoteMeta?.vehicleId || null,
      status: quoteMeta?.status || "Draft",
      companyId: account?.companyId,
    });
    storePreview(quote);
    window.open(
      `/quotation-detail?&token=preview&timezone=${
        logInUser?.attributes?.timezone || "Asia/Riyadh"
      }`,
      "_blank",
    );
  };

  const openSavedDocument = () => {
    if (!quoteMeta?.id) {
      preview();
      return;
    }
    window.open(
      `/quotation-detail?&token=${quoteMeta.id}&timezone=${
        logInUser?.attributes?.timezone || "Asia/Riyadh"
      }`,
      "_blank",
    );
  };

  const convertToManualInvoice = () => {
    if (!checkPrivileges("manualinvoiceCreate") || !onConvertToManualInvoice) {
      return;
    }
    if (currentStatus !== QUOTE_STATUS.APPROVED) {
      toast.error(translate("onlyApprovedQuoteToInvoice"));
      return;
    }
    if (isQuoteManualInvoiceCreated(quoteMeta) || isQuoteManualInvoiceCreated(form)) {
      toast.error(translate("quoteManualInvoiceAlreadyCreated"));
      return;
    }
    const formData = quoteToManualInvoiceForm({
      ...(quoteMeta || {}),
      ...form,
      account: form.account || quoteMeta?.account,
      items: form.items,
      status: currentStatus,
    });
    onConvertToManualInvoice(formData);
  };

  const canConvertToManualInvoice =
    typeof onConvertToManualInvoice === "function" &&
    checkPrivileges("manualinvoiceCreate") &&
    currentStatus === QUOTE_STATUS.APPROVED &&
    Boolean(quoteMeta?.id) &&
    !isQuoteManualInvoiceCreated(quoteMeta) &&
    !isQuoteManualInvoiceCreated(form);

  const disabled = readOnly || saving;

  return (
    <Box
      sx={{
        color: "#222",
        fontFamily: '"Open Sans", sans-serif',
        // bgcolor: Q.canvas,
        m: 0,
        p: "8px 0 24px",
        minHeight: "auto",
        "& .async-select .MuiOutlinedInput-root, & .async-select .MuiOutlinedInput-root.Mui-focused":
          {
            boxShadow: "none !important",
          },
        "& .async-select .MuiOutlinedInput-notchedOutline": {
          boxShadow: "none !important",
        },
      }}
    >
      {(form.quoteRefNo || quoteMeta?.quoteNumber || quoteMeta?.quoteRefNo) && (
        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 1.5,
            p: "14px 18px",
            mb: 1,
          }}
        >
          <Box>
            <Typography sx={{ display: "block", fontSize: 11, color: Q.muted, mb: 0.5 }}>
              {translate("quoteRefNo") || translate("quoteNumber")}
            </Typography>
            <Typography component="strong" sx={{ fontSize: 13, color: Q.navy }}>
              {form.quoteRefNo || quoteMeta?.quoteNumber || quoteMeta?.quoteRefNo}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ display: "block", fontSize: 11, color: Q.muted, mb: 0.5 }}>
              {translate("quoteDate")}
            </Typography>
            <Typography component="strong" sx={{ fontSize: 13, color: Q.navy }}>
              {moment(quoteMeta?.quoteDate || undefined).format("DD MMM YYYY")}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ display: "block", fontSize: 11, color: Q.muted, mb: 0.5 }}>
              {translate("currentDate")}
            </Typography>
            <Typography component="strong" sx={{ fontSize: 13, color: Q.navy }}>
              {moment().format("DD MMM YYYY HH:mm")}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ display: "block", fontSize: 11, color: Q.muted, mb: 0.5 }}>
              {translate("quotationExpiry")}
            </Typography>
            <Typography component="strong" sx={{ fontSize: 13, color: Q.navy }}>
              {quoteMeta?.expiryDate || form.expiryDate
                ? moment(quoteMeta?.expiryDate || form.expiryDate).format(
                    "DD MMM YYYY",
                  )
                : "-"}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ display: "block", fontSize: 11, color: Q.muted, mb: 0.5 }}>
              {translate("status")}
            </Typography>
            <Typography component="strong" sx={{ fontSize: 13, color: Q.navy }}>
              {translate(currentStatus) || currentStatus}
              {statusActor
                ? ` (${translate(
                    currentStatus === QUOTE_STATUS.APPROVED
                      ? "approvedBy"
                      : "rejectedBy",
                  )}: ${statusActor})`
                : ""}
            </Typography>
          </Box>
        </Paper>
      )}

      <SectionCard title={translate("customerDetails")}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label={translate("quoteRefNo") || translate("quoteNumber")}
              required
              disabled={disabled||mode==="edit"}
              value={form.quoteRefNo || ""}
              error={Boolean(fieldErrors.quoteRefNo)}
              onChange={(e) => handleChange("quoteRefNo", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <AsyncSelect
              selectName="account"
              isMulti={false}
              isClearable
              disabled={disabled}
              isLoading={accountLoading}
              value={form.account}
              placeholder={translate("accountName")}
              resultMessage={resultMessage}
              defaultOptions={accounts}
              handleChange={handleChange}
              menuPortalTarget={document.body}
              textFieldProps={{
                error: Boolean(fieldErrors.account),
                required: true,
              }}
              styles={{
                control: (base) => ({
                  ...base,
                  boxShadow: "none",
                }),
                menu: (base) => ({
                  ...base,
                  boxShadow: "none",
                }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              loadOptions={async (input) => {
                const rows = await fetchAccounts(input);
                setResultMessage(rows.length ? "" : "No option found");
                return rows;
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <TextField
                label={translate("email")}
                disabled={disabled}
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
                fullWidth
              />
              <Tooltip
                title={
                  translate("mailCheckboxTooltip") ||
                  "If checked, email will be sent"
                }
              >
                <FormControlLabel
                  sx={{ m: 0, whiteSpace: "nowrap", mt: 0.5 }}
                  control={
                    <Checkbox
                      checked={Boolean(form.mail)}
                      disabled={disabled}
                      onChange={(e) => handleChange("mail", e.target.checked)}
                    />
                  }
                  label={translate("mail") || "Mail"}
                />
              </Tooltip>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label={translate("contactPerson")}
              required
              disabled={disabled}
              value={form.contactPerson}
              error={Boolean(fieldErrors.contactPerson)}
              onChange={(e) => handleChange("contactPerson", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label={translate("mobileNumber") || translate("phone")}
              required
              disabled={disabled}
              value={form.mobile}
              error={Boolean(fieldErrors.mobile)}
              onChange={(e) => handleChange("mobile", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label={translate("address")}
              disabled={disabled}
              value={form.address}
              onChange={(e) => handleChange("address", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label={translate("vatNumber")}
              disabled={disabled}
              value={form.vatNumber}
              onChange={(e) => handleChange("vatNumber", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <CustomDatePicker
              format="DD/MM/YYYY"
              label={translate("quotationExpiry")}
              required
              disabled={disabled}
              minDate={new Date()}
              value={form.expiryDate}
              error={Boolean(fieldErrors.expiryDate)}
              onChange={(date) =>
                handleChange(
                  "expiryDate",
                  date ? moment(date).endOf("day").toISOString() : null,
                )
              }
              fullWidth
            />
          </Grid>
        </Grid>
      </SectionCard>

      <VehicleDetailsSection
        form={form}
        setForm={setForm}
        fieldErrors={fieldErrors}
        clearFieldError={clearFieldError}
        disabled={disabled || mode === "edit"}
        translate={translate}
        logInUser={logInUser}
      />

      <SectionCard title={translate("lineItems")}>
        {form.items.map((item, index) => (
          <Grid
            container
            spacing={1}
            key={index}
            style={{
              marginBottom: 8,
              paddingBottom: 8,
              borderBottom: "1px dashed #e2e9ef",
            }}
          >
            <Grid item xs={12} md={2}>
              <TextField
                select
                label={translate("type")}
                required
                disabled={disabled || !form.contractNumber}
                value={item.expenseTypeId || ""}
                error={Boolean(fieldErrors.items?.[index]?.serviceType)}
                onChange={(e) => {
                  const selected = allExpenseTypes.find(
                    (option) => String(option.id) === String(e.target.value),
                  );
                  handleLineChange("serviceType", selected || "", index);
                }}
                fullWidth
              >
                <MenuItem value="">
                  <em>{translate("none")}</em>
                </MenuItem>
                {allExpenseTypes.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label || option.name}
                  </MenuItem>
                ))}
                {item.expenseTypeId &&
                  !allExpenseTypes.some(
                    (option) =>
                      String(option.id) === String(item.expenseTypeId),
                  ) && (
                    <MenuItem value={item.expenseTypeId}>
                      {item.typeLabel ||
                        item.serviceType ||
                        (typeof item.expenseType === "object"
                          ? item.expenseType.label || item.expenseType.name
                          : item.expenseType) ||
                        item.expenseTypeId}
                    </MenuItem>
                  )}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3} style={{ zIndex: 8 }}>
              <SingleSelect
                key={`${index}-${item.expenseTypeId || item.serviceType}`}
                array={lineOptions[item.expenseTypeId] || []}
                async
                isClearable
                selectName="item"
                loadOptions={(inputValue) =>
                  searchExpenseSubTypes(
                    inputValue,
                    item.expenseType || { id: item.expenseTypeId },
                  )
                }
                label={translate("subTypes")}
                value={
                  item.item
                    ? {
                        id: item.item.id || item.itemId,
                        label: item.item.label || item.itemName,
                      }
                    : item.itemName
                      ? { id: item.itemId, label: item.itemName }
                      : ""
                }
                index={index}
                isDisabled={disabled || !item.expenseTypeId}
                isLoading={Boolean(subTypeLoading[item.expenseTypeId])}
                handleChange={handleLineChange}
                canRemove
                canAssign
                menuPlacement="auto"
                menuPortalTarget={document.body}
                styles={withSelectError(fieldErrors.items?.[index]?.itemName, {
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                })}
              />
            </Grid>
            <Grid item xs={6} md={1}>
              <TextField
                type="number"
                label={translate("quantity")}
                disabled={disabled}
                value={item.quantity}
                error={Boolean(fieldErrors.items?.[index]?.quantity)}
                onChange={(e) =>
                  handleLineChange("quantity", e.target.value, index)
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                type="number"
                label={translate("price")}
                disabled={disabled}
                value={item.unitPrice}
                error={Boolean(fieldErrors.items?.[index]?.unitPrice)}
                onChange={(e) =>
                  handleLineChange("unitPrice", e.target.value, index)
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label={translate("description")}
                disabled={disabled}
                value={item.description}
                onChange={(e) =>
                  handleLineChange("description", e.target.value, index)
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={2} style={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(item.withVat)}
                    disabled={disabled}
                    onChange={(e) =>
                      handleLineChange("withVat", e.target.checked, index)
                    }
                  />
                }
                label={item.withVat ? translate("withVat") : translate("withoutVat")}
              />
              {!disabled && (
                <>
                  <Tooltip title={translate("add")}>
                    <IconButton size="small" onClick={addLine}>
                      <Add fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={translate("delete")}>
                    <IconButton size="small" onClick={() => removeLine(index)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Grid>
          </Grid>
        ))}
      </SectionCard>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <SectionCard title={translate("salesManager")}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} style={{ zIndex: 7 }}>
                <AsyncSelect
                  selectName="salesPerson"
                  isMulti={false}
                  isClearable
                  disabled={disabled}
                  value={
                    form.salesPerson
                      ? {
                          id: form.salesPerson.id,
                          value: form.salesPerson.id,
                          label:
                            form.salesPerson.label || form.salesPerson.name,
                          name:
                            form.salesPerson.name || form.salesPerson.label,
                          email: form.salesPerson.email || form.salesPersonEmail,
                          phone:
                            form.salesPerson.phone ||
                            form.salesPerson.mobile ||
                            form.salesPersonMobile,
                        }
                      : null
                  }
                  placeholder={translate("salesManager")}
                  defaultOptions={salesPeople}
                  handleChange={handleChange}
                  menuPortalTarget={document.body}
                  textFieldProps={{
                    error: Boolean(fieldErrors.salesPerson),
                    required: true,
                  }}
                  styles={withSelectError(fieldErrors.salesPerson, {
                    control: (base) => ({
                      ...base,
                      boxShadow: "none",
                    }),
                    menu: (base) => ({
                      ...base,
                      boxShadow: "none",
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  })}
                  loadOptions={async (input) => {
                    const selectedLabel =
                      form.salesPerson?.label || form.salesPerson?.name || "";
                    const search =
                      input && String(input) !== String(selectedLabel)
                        ? input
                        : "";
                    const rows = await fetchSalesPeople(search, form.salesPerson);
                    setResultMessage(rows.length ? "" : "No option found");
                    return rows;
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={translate("email")}
                  disabled
                  value={form.salesPersonEmail}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={translate("phone")}
                  disabled
                  value={form.salesPersonMobile}
                  fullWidth
                />
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ ...cardSx, p: "16px 18px" }}>
            <Stack spacing={1}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 13,
                }}
              >
                <span>{translate("subtotal")}</span>
                <strong>{totals.subtotal.toFixed(2)}</strong>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 13,
                }}
              >
                <span>
                  {translate("vat")} {Math.round(vatPercentage)}%
                </span>
                <strong>{totals.vatTotal.toFixed(2)}</strong>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 13,
                  fontWeight: 700,
                  color: Q.navy,
                  borderTop: "1px solid",
                  borderColor: Q.line,
                  pt: 1,
                  mt: 1,
                }}
              >
                <span>{translate("grandTotal")}</span>
                <strong>
                  <CurrencySymbol
                    currency={currencyLabel}
                    withParentheses={false}
                  />{" "}
                  {totals.grandTotal.toFixed(2)}
                </strong>
              </Box>
            </Stack>
            <Typography sx={{ fontSize: 11, color: Q.muted, mt: 1 }}>
              {translate("noFinancialImpact")}
            </Typography>
            <Button
              variant="outlined"
              onClick={quoteMeta?.id ? openSavedDocument : preview}
              style={{ marginTop: 8 }}
            >
              {translate("openTerms")}
            </Button>
          </Paper>
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{
          ...cardSx,
          display: "flex",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: 1,
          mt: 2,
          p: "16px 18px",
        }}
      >
        <Button
          variant="outlined"
          onClick={quoteMeta?.id ? openSavedDocument : preview}
        >
          {translate("previewDocument")}
        </Button>
        {canConvertToManualInvoice && (
          <Button variant="outlined" onClick={convertToManualInvoice}>
            {translate("convertToManualInvoice")}
          </Button>
        )}
        {readOnly ? (
          !isStatusLocked ? (
            <Button onClick={() => setReadOnly(false)}>
              {translate("sharedEdit") || translate("edit")}
            </Button>
          ) : null
        ) : (
          <Button onClick={persist} disabled={saving}>
            {quoteMeta?.quoteNumber
              ? translate("sharedUpdate")
              : translate("createQuote")}
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default CreateUpdateForm;
